'use client';

/**
 * Organiser sign in, on Clerk.
 *
 * Built on Clerk's headless sign-in resource (useSignIn) rather than the
 * Clerk Elements step components, because the handoff's flow needs things
 * Elements has no slot for: going straight from the password step to an
 * emailed reset code (Elements puts a "choose a strategy" screen in
 * between), and every error in our own plain words rather than Clerk's.
 * Nothing Clerk-branded is rendered.
 *
 * Sign in is email and password, or Google or Apple. Steps: email, then
 * password (a forgotten one is reset with an emailed code, then a new
 * password), a second-factor code if the account has one, and done.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth, useSignIn } from '@clerk/nextjs';
import {
  authErrorMessage,
  clerkErrorCode,
  emailFieldError,
  isCompleteCode,
  passwordRuleMet,
  PASSWORD_MIN_LENGTH,
  safeRedirectPath,
  sanitiseCode,
} from '@/lib/auth/auth-flow';
import {
  AthleteNote,
  AuthFooter,
  AuthHeader,
  AuthIconTile,
  AuthIntro,
  AuthMain,
  AuthPage,
  AuthTitle,
  CodeInput,
  EmailChip,
  FieldLabel,
  FieldMessage,
  MailIcon,
  OrDivider,
  PasswordInput,
  PrimaryButton,
  ProviderButtons,
  TextAction,
  TextInput,
  TickBox,
  TickIcon,
  PasswordRule,
} from './parts';
import {
  applyKeepSignedIn,
  recallEmail,
  rememberEmail,
} from './session';

/** Clerk's sign-in resource, taken from the hook so no extra package is needed. */
type SignInResource = NonNullable<ReturnType<typeof useSignIn>['signIn']>;

type Step = 'email' | 'password' | 'code' | 'new-password' | 'done';
/** What the code step is for. */
type CodePurpose = 'code' | 'reset' | 'second';
type Via = 'password' | 'code' | 'oauth';
type SecondFactor = 'totp' | 'phone_code' | 'email_code' | 'backup_code';

const DEFAULT_DESTINATION = '/dashboard';

function emailFactorId(signIn: SignInResource | null | undefined, strategy: 'email_code' | 'reset_password_email_code'): string | null {
  const factor = signIn?.supportedFirstFactors?.find((f) => f.strategy === strategy) as { emailAddressId?: string } | undefined;
  return factor?.emailAddressId ?? null;
}

/**
 * `preview` renders one step with no Clerk state behind it, for the visual
 * QA pages only. The real routes never pass it.
 */
export type SignInPreview = { step: Step; email: string; purpose?: CodePurpose; error?: string };

export function OrganiserSignIn({ preview }: { preview?: SignInPreview } = {}) {
  const router = useRouter();
  const params = useSearchParams();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded, signIn, setActive } = useSignIn();

  const [step, setStep] = useState<Step>(preview?.step ?? 'email');
  const [email, setEmail] = useState(preview?.email ?? '');
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [pwErr, setPwErr] = useState<string | null>(preview?.step === 'password' ? preview.error ?? null : null);
  const [showPw, setShowPw] = useState(false);
  const [keep, setKeep] = useState(true);
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [purpose, setPurpose] = useState<CodePurpose>(preview?.purpose ?? 'code');
  const [secondFactor, setSecondFactor] = useState<SecondFactor | null>(null);
  const [resent, setResent] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPwErr, setNewPwErr] = useState<string | null>(null);
  const [providerErr, setProviderErr] = useState<string | null>(null);
  const [altErr, setAltErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const finishing = useRef(false);
  /** How the first step was passed, so the second factor ends the same way (keep-signed-in applies to passwords only). */
  const firstVia = useRef<Via>('password');

  const destination =
    (typeof window !== 'undefined' && safeRedirectPath(params.get('redirect_url'), window.location.origin)) || DEFAULT_DESTINATION;

  // The email typed on the sign-up screen comes across.
  useEffect(() => {
    const carried = recallEmail();
    if (carried) setEmail(carried);
  }, []);

  // Already signed in (and not mid-way through our own done step): go on.
  useEffect(() => {
    if (!preview && authLoaded && isSignedIn && step !== 'done' && !finishing.current) {
      router.replace(destination);
    }
  }, [preview, authLoaded, isSignedIn, step, destination, router]);

  const go = useCallback(() => router.replace(destination), [router, destination]);

  /** A session exists: set it and go on. */
  const complete = useCallback(
    async (res: SignInResource, via: Via) => {
      finishing.current = true;
      await setActive?.({ session: res.createdSessionId });
      if (via === 'password' || via === 'code') applyKeepSignedIn(keep);
      setStep('done');
      go();
    },
    [setActive, keep, go],
  );

  /** Route a sign-in result to the next step. */
  const handleResult = useCallback(
    async (res: SignInResource, via: Via) => {
      if (res.status === 'complete') return complete(res, via);
      if (res.status === 'needs_new_password') {
        setStep('new-password');
        return;
      }
      if (res.status === 'needs_second_factor') {
        const factors = res.supportedSecondFactors ?? [];
        const pick = (['totp', 'phone_code', 'email_code', 'backup_code'] as SecondFactor[]).find((s) =>
          factors.some((f) => f.strategy === s),
        );
        if (!pick) throw new Error('No second factor available');
        if (pick === 'phone_code' || pick === 'email_code') {
          const f = factors.find((x) => x.strategy === pick) as { phoneNumberId?: string; emailAddressId?: string } | undefined;
          await res.prepareSecondFactor(
            pick === 'phone_code'
              ? { strategy: 'phone_code', phoneNumberId: f?.phoneNumberId }
              : ({ strategy: 'email_code', emailAddressId: f?.emailAddressId } as Parameters<SignInResource['prepareSecondFactor']>[0]),
          );
        }
        firstVia.current = via;
        setSecondFactor(pick);
        setPurpose('second');
        setCode('');
        setCodeErr(null);
        setResent(false);
        setStep('code');
        return;
      }
      throw new Error(`Unexpected sign-in status ${res.status}`);
    },
    [complete],
  );

  // Back from Google or Apple on an account with two-step verification:
  // Clerk lands here mid sign-in, so carry on at the code step.
  useEffect(() => {
    if (preview || !isLoaded || !signIn || signIn.status !== 'needs_second_factor') return;
    void (async () => {
      setEmail(signIn.identifier ?? '');
      try {
        await handleResult(signIn, 'oauth');
      } catch {
        // Nothing to resume: they start again from the email step.
      }
    })();
    // Once, when Clerk has loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  const fail = (setter: (m: string | null) => void, context: Parameters<typeof authErrorMessage>[1]) => (err: unknown) => {
    const { code: c, status } = clerkErrorCode(err);
    setter(authErrorMessage(c, context, status));
  };

  /* ── Actions ── */

  const submitEmail = async () => {
    const problem = emailFieldError(email);
    if (problem) return setEmailErr(problem);
    if (!isLoaded || !signIn) return;
    setBusy('email');
    setEmailErr(null);
    try {
      const res = await signIn.create({ identifier: email.trim() });
      const factors = res.supportedFirstFactors ?? [];
      setPassword('');
      setPwErr(null);
      setAltErr(null);
      if (factors.some((f) => f.strategy === 'password')) {
        setStep('password');
      } else if (factors.some((f) => f.strategy === 'email_code')) {
        await res.prepareFirstFactor({ strategy: 'email_code', emailAddressId: emailFactorId(res, 'email_code')! });
        openCode('code');
      } else if (factors.some((f) => String(f.strategy).startsWith('oauth_'))) {
        setEmailErr('This account signs in with Google or Apple. Use the button above.');
      } else {
        setEmailErr(authErrorMessage(null, 'email'));
      }
    } catch (err) {
      fail(setEmailErr, 'email')(err);
    } finally {
      setBusy(null);
    }
  };

  const openCode = (p: CodePurpose) => {
    setPurpose(p);
    setCode('');
    setCodeErr(null);
    setResent(false);
    setStep('code');
  };

  const submitPassword = async () => {
    if (!password) return setPwErr('Enter your password.');
    if (!signIn) return;
    setBusy('password');
    setPwErr(null);
    try {
      const res = await signIn.attemptFirstFactor({ strategy: 'password', password });
      await handleResult(res, 'password');
    } catch (err) {
      fail(setPwErr, 'password')(err);
    } finally {
      setBusy(null);
    }
  };

  const forgot = async () => {
    if (!signIn) return;
    const id = emailFactorId(signIn, 'reset_password_email_code');
    if (!id) return setPwErr("Password reset isn't available for this account. Use an email code instead.");
    setBusy('forgot');
    setPwErr(null);
    try {
      await signIn.prepareFirstFactor({ strategy: 'reset_password_email_code', emailAddressId: id });
      openCode('reset');
    } catch (err) {
      fail(setPwErr, 'password')(err);
    } finally {
      setBusy(null);
    }
  };

  const submitCode = async () => {
    if (!signIn || !isCompleteCode(code)) return;
    setBusy('code');
    setCodeErr(null);
    try {
      if (purpose === 'second' && secondFactor) {
        const res = await signIn.attemptSecondFactor({ strategy: secondFactor, code } as Parameters<SignInResource['attemptSecondFactor']>[0]);
        await handleResult(res, firstVia.current);
      } else if (purpose === 'reset') {
        const res = await signIn.attemptFirstFactor({ strategy: 'reset_password_email_code', code });
        await handleResult(res, 'password');
      } else {
        const res = await signIn.attemptFirstFactor({ strategy: 'email_code', code });
        await handleResult(res, 'code');
      }
    } catch (err) {
      fail(setCodeErr, 'code')(err);
    } finally {
      setBusy(null);
    }
  };

  const resend = async () => {
    if (!signIn) return;
    setCodeErr(null);
    try {
      if (purpose === 'second' && (secondFactor === 'phone_code' || secondFactor === 'email_code')) {
        const f = signIn.supportedSecondFactors?.find((x) => x.strategy === secondFactor) as { phoneNumberId?: string; emailAddressId?: string } | undefined;
        await signIn.prepareSecondFactor(
          secondFactor === 'phone_code'
            ? { strategy: 'phone_code', phoneNumberId: f?.phoneNumberId }
            : ({ strategy: 'email_code', emailAddressId: f?.emailAddressId } as Parameters<SignInResource['prepareSecondFactor']>[0]),
        );
      } else if (purpose === 'reset') {
        await signIn.prepareFirstFactor({ strategy: 'reset_password_email_code', emailAddressId: emailFactorId(signIn, 'reset_password_email_code')! });
      } else {
        await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: emailFactorId(signIn, 'email_code')! });
      }
      setResent(true);
    } catch (err) {
      fail(setCodeErr, 'code')(err);
    }
  };

  const submitNewPassword = async () => {
    if (!passwordRuleMet(newPassword)) return setNewPwErr(`Use at least ${PASSWORD_MIN_LENGTH} characters.`);
    if (!signIn) return;
    setBusy('new-password');
    setNewPwErr(null);
    try {
      const res = await signIn.resetPassword({ password: newPassword, signOutOfOtherSessions: true });
      await handleResult(res, 'password');
    } catch (err) {
      fail(setNewPwErr, 'new-password')(err);
    } finally {
      setBusy(null);
    }
  };

  const startProvider = async (provider: 'google' | 'apple') => {
    if (!signIn) return;
    setBusy(provider);
    setProviderErr(null);
    try {
      await signIn.authenticateWithRedirect({
        strategy: provider === 'google' ? 'oauth_google' : 'oauth_apple',
        redirectUrl: '/sign-in/sso-callback',
        redirectUrlComplete: destination,
      });
    } catch (err) {
      fail(setProviderErr, 'provider')(err);
      setBusy(null);
    }
  };

  const backToEmail = () => {
    setStep('email');
    setCode('');
    setPassword('');
    setPwErr(null);
    setCodeErr(null);
    setAltErr(null);
  };

  const toSignUp = () => {
    rememberEmail(email);
    router.push('/sign-up');
  };

  /* ── Screens ── */

  const codeTitle = purpose === 'reset' ? 'Reset your password' : purpose === 'second' ? 'One more check' : 'Check your email';
  const codeAction = purpose === 'reset' ? 'Continue' : 'Sign in';
  const codeText =
    purpose === 'second' && secondFactor === 'totp'
      ? 'Enter the 6-digit code from your authenticator app.'
      : purpose === 'second' && secondFactor === 'backup_code'
        ? 'Enter one of your backup codes.'
        : purpose === 'second' && secondFactor === 'phone_code'
          ? 'We sent a 6-digit code to your phone. It works for 10 minutes.'
          : null;

  return (
    <AuthPage>
      <AuthHeader
        prompt={step === 'done' ? undefined : 'New to Fitvent?'}
        action={step === 'done' ? undefined : { label: 'Create an account', onClick: toSignUp }}
      />
      <AuthMain>
        {step === 'email' && (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submitEmail();
            }}
          >
            <AuthTitle>Sign in to Fitvent</AuthTitle>
            <AuthIntro>Welcome back to your organiser account.</AuthIntro>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 28 }}>
              <ProviderButtons
                onGoogle={() => startProvider('google')}
                onApple={() => startProvider('apple')}
                busy={busy === 'google' || busy === 'apple' ? busy : null}
              />
            </div>
            {providerErr && <FieldMessage tone="error">{providerErr}</FieldMessage>}

            <OrDivider />

            <FieldLabel htmlFor="signin-email">Email</FieldLabel>
            <TextInput
              id="signin-email"
              type="email"
              inputMode="email"
              autoComplete="username"
              placeholder="you@example.com"
              value={email}
              invalid={!!emailErr}
              aria-invalid={!!emailErr || undefined}
              aria-describedby={emailErr ? 'signin-email-err' : undefined}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailErr(null);
              }}
            />
            {emailErr && (
              <FieldMessage id="signin-email-err" tone="error">
                {emailErr}
              </FieldMessage>
            )}
            <PrimaryButton loading={busy === 'email'}>Continue</PrimaryButton>

            <AthleteNote actionLabel="Sign in to myfitvent" />
          </form>
        )}

        {step === 'password' && (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submitPassword();
            }}
          >
            <EmailChip email={email.trim()} onClick={backToEmail} />
            <AuthTitle gap={20}>Enter your password</AuthTitle>

            {/* The username field lets password managers pair the saved login. */}
            <input type="email" autoComplete="username" value={email.trim()} readOnly hidden />
            <div style={{ marginTop: 26 }}>
              <FieldLabel
                htmlFor="signin-password"
                aside={
                  <span style={{ fontSize: 13 }}>
                    <TextAction onClick={forgot} disabled={busy === 'forgot'}>
                      Forgot it?
                    </TextAction>
                  </span>
                }
              >
                Password
              </FieldLabel>
              <PasswordInput
                id="signin-password"
                value={password}
                onChange={(v) => {
                  setPassword(v);
                  setPwErr(null);
                }}
                autoComplete="current-password"
                invalid={!!pwErr}
                shown={showPw}
                onToggle={() => setShowPw((s) => !s)}
                describedBy={pwErr ? 'signin-password-err' : undefined}
              />
              {pwErr && (
                <FieldMessage id="signin-password-err" tone="error">
                  {pwErr}
                </FieldMessage>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <TickBox checked={keep} onChange={setKeep}>
                Keep me signed in on this device
              </TickBox>
            </div>

            <PrimaryButton loading={busy === 'password'} marginTop={16}>
              Sign in
            </PrimaryButton>
            {altErr && <FieldMessage tone="error">{altErr}</FieldMessage>}
          </form>
        )}

        {step === 'code' && (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submitCode();
            }}
          >
            <AuthIconTile>
              <MailIcon />
            </AuthIconTile>
            <AuthTitle gap={18}>{codeTitle}</AuthTitle>
            <AuthIntro>
              {codeText ?? (
                <>
                  We sent a 6-digit code to <span style={{ color: 'var(--fvd-ink)', fontWeight: 500 }}>{email.trim()}</span>. It works for 10 minutes.
                </>
              )}
            </AuthIntro>
            <CodeInput
              value={code}
              onChange={(raw) => {
                setCode(secondFactor === 'backup_code' && purpose === 'second' ? raw.trim().slice(0, 32) : sanitiseCode(raw));
                setCodeErr(null);
              }}
              invalid={!!codeErr}
              describedBy={codeErr ? 'signin-code-err' : undefined}
              autoFocus
            />
            {codeErr && (
              <FieldMessage id="signin-code-err" tone="error">
                {codeErr}
              </FieldMessage>
            )}
            <PrimaryButton
              disabled={secondFactor === 'backup_code' && purpose === 'second' ? code.length < 6 : !isCompleteCode(code)}
              loading={busy === 'code'}
            >
              {codeAction}
            </PrimaryButton>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 18, fontSize: 13, color: 'var(--fvd-ink-secondary)' }}>
              {!(purpose === 'second' && (secondFactor === 'totp' || secondFactor === 'backup_code')) && (
                <>
                  {resent ? (
                    <span>Sent again. Check spam if it&rsquo;s not there.</span>
                  ) : (
                    <>
                      <span>Didn&rsquo;t get it?</span>
                      <TextAction onClick={resend}>Send it again</TextAction>
                    </>
                  )}
                  <span aria-hidden="true" style={{ color: 'var(--fvd-ink-disabled)' }}>
                    ·
                  </span>
                </>
              )}
              <TextAction onClick={backToEmail}>Use a different email</TextAction>
            </div>
          </form>
        )}

        {step === 'new-password' && (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submitNewPassword();
            }}
          >
            <EmailChip email={email.trim()} onClick={backToEmail} />
            <AuthTitle gap={20}>Choose a new password</AuthTitle>
            <input type="email" autoComplete="username" value={email.trim()} readOnly hidden />
            <div style={{ marginTop: 26 }}>
              <FieldLabel htmlFor="signin-new-password">New password</FieldLabel>
              <PasswordInput
                id="signin-new-password"
                value={newPassword}
                onChange={(v) => {
                  setNewPassword(v);
                  setNewPwErr(null);
                }}
                autoComplete="new-password"
                invalid={!!newPwErr}
                shown={showPw}
                onToggle={() => setShowPw((s) => !s)}
              />
              <PasswordRule met={passwordRuleMet(newPassword)} />
              {newPwErr && <FieldMessage tone="error">{newPwErr}</FieldMessage>}
            </div>
            <PrimaryButton disabled={!passwordRuleMet(newPassword)} loading={busy === 'new-password'} marginTop={22}>
              Save and sign in
            </PrimaryButton>
          </form>
        )}

        {step === 'done' && (
          <div>
            <AuthIconTile round>
              <TickIcon />
            </AuthIconTile>
            <AuthTitle gap={18}>Signed in</AuthTitle>
            <AuthIntro>Taking you to your dashboard.</AuthIntro>
          </div>
        )}
      </AuthMain>
      <AuthFooter />
    </AuthPage>
  );
}
